import React, { useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../auth/AuthContext";
import { t } from "../i18n/translations";
import { useLanguage } from "../i18n/LanguageContext";

const isHttpUrl = (s) => {
    if (!s) return false;
    try {
        const url = new URL(String(s).trim());
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
};

const parseTags = (value) =>
    String(value || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

const createEmptyMemberForm = () => ({
    id: "",
    name: "",
    prefix: "",
    bio: "",
    email: "",
    role: "member",
    approved: false,
    verifiedBy: "",
    photoURL: ""
});

function ErrorMessage({ message }) {
    if (!message) return null;
    return (
        <div className="rounded-xl bg-red-50 text-red-600 px-4 py-2 text-sm border border-red-100">
            {message}
        </div>
    );
}

function SuccessMessage({ message }) {
    if (!message) return null;
    return (
        <div className="rounded-xl bg-emerald-50 text-emerald-700 px-4 py-2 text-sm border border-emerald-100">
            {message}
        </div>
    );
}

export function LoginPage() {
    const navigate = useNavigate();
    const { login, currentUser, loading } = useAuth();
    const [form, setForm] = useState({ email: "", password: "" });
    const [error, setError] = useState("");
    const [pending, setPending] = useState(false);
    const { language } = useLanguage();

    if (loading) {
        return (
            <section className="py-16">
                <div className="max-w-md mx-auto text-center text-muted-foreground">
                    {t("authPages.loading", language)}
                </div>
            </section>
        );
    }

    if (currentUser) {
        return <Navigate to="/account" replace />;
    }

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError("");
        setPending(true);
        const result = await login(form.email, form.password);
        setPending(false);
        if (!result.ok) {
            setError(result.error);
            return;
        }
        navigate("/account");
    };

    return (
        <section className="py-16">
            <div className="max-w-md mx-auto">
                <Card className="rounded-3xl shadow-sm">
                    <CardHeader>
                        <CardTitle className="text-2xl">{t("auth.login", language)}</CardTitle>
                        <p className="text-sm text-muted-foreground">{t("authPages.login.subtitle", language)}</p>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="text-sm font-medium" htmlFor="email">
                                    {t("contact.email", language)}
                                </label>
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={handleChange}
                                    placeholder={t("authPages.login.emailPlaceholder", language)}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium" htmlFor="password">
                                    {t("authPages.login.passwordLabel", language)}
                                </label>
                                <Input
                                    id="password"
                                    name="password"
                                    type="password"
                                    required
                                    value={form.password}
                                    onChange={handleChange}
                                />
                            </div>
                            <ErrorMessage message={error} />
                            <Button type="submit" className="w-full rounded-2xl" disabled={pending}>
                                {pending
                                    ? t("authPages.login.pending", language)
                                    : t("authPages.login.action", language)}
                            </Button>
                        </form>
                        <p className="mt-6 text-sm text-muted-foreground text-center">
                            {t("authPages.login.footerPrompt", language)}{" "}
                            <Link className="text-primary font-medium" to="/signup">
                                {t("auth.signup", language)}
                            </Link>
                        </p>
                    </CardContent>
                </Card>
            </div>
        </section>
    );
}

export function SignupPage() {
    const navigate = useNavigate();
    const { signup, currentUser, loading } = useAuth();
    const [form, setForm] = useState({ email: "", password: "", confirmPassword: "" });
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [pending, setPending] = useState(false);
    const { language } = useLanguage();

    if (loading) {
        return (
            <section className="py-16">
                <div className="max-w-md mx-auto text-center text-muted-foreground">
                    {t("authPages.loading", language)}
                </div>
            </section>
        );
    }

    if (currentUser) {
        return <Navigate to="/account" replace />;
    }

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError("");
        setSuccess("");
        if (form.password !== form.confirmPassword) {
            setError(t("authPages.signup.mismatch", language));
            return;
        }
        setPending(true);
        const result = await signup({ email: form.email, password: form.password });
        setPending(false);
        if (!result.ok) {
            setError(result.error);
            return;
        }
        setSuccess(t("authPages.signup.success", language));
        setTimeout(() => navigate("/account"), 800);
    };

    return (
        <section className="py-16">
            <div className="max-w-md mx-auto">
                <Card className="rounded-3xl shadow-sm">
                    <CardHeader>
                        <CardTitle className="text-2xl">{t("authPages.signup.title", language)}</CardTitle>
                        <p className="text-sm text-muted-foreground">{t("authPages.signup.subtitle", language)}</p>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label className="text-sm font-medium" htmlFor="signup-email">
                                    {t("contact.email", language)}
                                </label>
                                <Input
                                    id="signup-email"
                                    name="email"
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={handleChange}
                                    placeholder={t("authPages.signup.emailPlaceholder", language)}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium" htmlFor="signup-password">
                                    {t("authPages.login.passwordLabel", language)}
                                </label>
                                <Input
                                    id="signup-password"
                                    name="password"
                                    type="password"
                                    required
                                    value={form.password}
                                    onChange={handleChange}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium" htmlFor="signup-confirm">
                                    {t("authPages.signup.confirmLabel", language)}
                                </label>
                                <Input
                                    id="signup-confirm"
                                    name="confirmPassword"
                                    type="password"
                                    required
                                    value={form.confirmPassword}
                                    onChange={handleChange}
                                />
                            </div>
                            <ErrorMessage message={error} />
                            <SuccessMessage message={success} />
                            <Button type="submit" className="w-full rounded-2xl" disabled={pending}>
                                {pending
                                    ? t("authPages.signup.pending", language)
                                    : t("authPages.signup.action", language)}
                            </Button>
                            <p className="text-sm text-muted-foreground text-center">
                                {t("authPages.signup.footerPrompt", language)}{" "}
                                <Link className="text-primary font-medium" to="/login">
                                    {t("auth.login", language)}
                                </Link>
                            </p>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </section>
    );
}

export function AccountPage() {
    const {
        currentUser,
        logout,
        teamMembers,
        loading,
        createTeamMember,
        updateTeamMember,
        deleteTeamMember,
        books,
        resources,
        createBook,
        updateBook,
        deleteBook,
        createResource,
        updateResource,
        deleteResource
    } = useAuth();
    const { language } = useLanguage();
    const [form, setForm] = useState(() => createEmptyMemberForm());
    const [editingMemberId, setEditingMemberId] = useState(null);
    const [formError, setFormError] = useState("");
    const [formSuccess, setFormSuccess] = useState("");
    const [actionError, setActionError] = useState("");
    const [pending, setPending] = useState(false);

    const isAdmin = currentUser?.role === "admin";
    const locale = language === "ar" ? "ar" : language === "he" ? "he" : "en";

    const sortedMembers = useMemo(() => {
        return [...teamMembers].sort((a, b) => (a.name || "").localeCompare(b.name || "", locale));
    }, [teamMembers, locale]);

    if (loading) {
        return (
            <section className="py-16">
                <div className="max-w-md mx-auto text-center text-muted-foreground">
                    {t("accountPage.loading", language)}
                </div>
            </section>
        );
    }

    if (!currentUser) {
        return <Navigate to="/login" replace />;
    }

    const handleFormChange = (event) => {
        const { name, value, type, checked } = event.target;
        setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    };

    const resetForm = () => {
        setForm(createEmptyMemberForm());
        setEditingMemberId(null);
    };

    const beginEditMember = (member) => {
        setForm({
            id: member.id || "",
            name: member.name || "",
            prefix: member.prefix || "",
            bio: member.bio || "",
            email: member.email || "",
            role: member.role || "member",
            approved: !!member.approved,
            verifiedBy: member.verifiedBy || "",
            photoURL: member.photoURL || ""
        });
        setEditingMemberId(member.id);
        setFormError("");
        setFormSuccess("");
        setActionError("");
    };

    const cancelEditMember = () => {
        resetForm();
        setFormError("");
        setFormSuccess("");
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setFormError("");
        setFormSuccess("");
        const trimmedId = form.id.trim();
        const trimmedName = form.name.trim();
        const trimmedEmail = form.email.trim();
        if ((!editingMemberId && !trimmedId) || !trimmedName || !trimmedEmail) {
            setFormError(t("accountPage.addMember.error.required", language));
            return;
        }
        setPending(true);
        const payload = {
            name: trimmedName,
            prefix: form.prefix.trim(),
            bio: form.bio.trim(),
            email: trimmedEmail.toLowerCase(),
            role: form.role.trim() || "member",
            approved: form.approved,
            verifiedBy: form.verifiedBy.trim(),
            photoURL: form.photoURL.trim()
        };
        const result = editingMemberId
            ? await updateTeamMember(editingMemberId, payload)
            : await createTeamMember({ ...payload, id: trimmedId });
        setPending(false);
        if (!result.ok) {
            setFormError(result.error);
            return;
        }
        setFormSuccess(
            editingMemberId
                ? t("accountPage.addMember.success.updated", language)
                : t("accountPage.addMember.success.created", language)
        );
        resetForm();
    };

    const handleToggleApproved = async (member) => {
        setActionError("");
        const result = await updateTeamMember(member.id, { approved: !member.approved });
        if (!result.ok) {
            setActionError(result.error);
            return;
        }
        if (editingMemberId === member.id) {
            setForm((prev) => ({ ...prev, approved: !member.approved }));
        }
    };

    const handleDelete = async (member) => {
        setActionError("");
        const result = await deleteTeamMember(member.id);
        if (!result.ok) {
            setActionError(result.error);
            return;
        }
        if (editingMemberId === member.id) {
            cancelEditMember();
        }
    };

    return (
        <section className="py-16 space-y-8">
            <div className="max-w-3xl mx-auto space-y-4">
                <Card className="rounded-3xl shadow-sm">
                    <CardHeader>
                        <CardTitle className="text-2xl">{t("auth.myAccount", language)}</CardTitle>
                        <p className="text-sm text-muted-foreground">{t("accountPage.subtitle", language)}</p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="rounded-2xl border border-slate-200 p-4 text-sm dark:border-slate-800">
                            <div className="font-medium">{currentUser.email}</div>
                            <div className="text-muted-foreground">
                                {t("accountPage.roleLabel", language)}:{" "}
                                {t(currentUser.role === "admin" ? "auth.admin" : "team.member", language)}
                            </div>
                        </div>
                        <Button type="button" className="rounded-2xl" onClick={logout}>
                            {t("auth.signout", language)}
                        </Button>
                    </CardContent>
                </Card>

                <Card className="rounded-3xl shadow-sm">
                    <CardHeader>
                        <CardTitle className="text-xl">{t("accountPage.team.title", language)}</CardTitle>
                        <p className="text-sm text-muted-foreground">
                            {isAdmin
                                ? t("accountPage.team.adminDescription", language)
                                : t("accountPage.team.memberDescription", language)}
                        </p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-xs text-muted-foreground">{t("accountPage.team.realtime", language)}</p>
                        <ErrorMessage message={actionError} />
                        {sortedMembers.length === 0 ? (
                            <div className="text-sm text-muted-foreground">
                                {t("accountPage.team.empty", language)}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {sortedMembers.map((member) => {
                                    const displayName = [member.prefix, member.name].filter(Boolean).join(" ").trim();
                                    return (
                                        <div
                                            key={member.id}
                                            className="rounded-2xl border border-slate-200 p-4 text-sm flex flex-col gap-2 dark:border-slate-800"
                                        >
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                <div>
                                                    <div className="font-medium">
                                                        {displayName || t("team.member", language)}
                                                    </div>
                                                    <div className="text-muted-foreground">
                                                        {member.email} • {t(member.role === "admin" ? "auth.admin" : "team.member", language)}
                                                    </div>
                                                </div>
                                                <div className="text-xs font-medium uppercase tracking-wide">
                                                    {member.approved ? t("team.approved", language) : t("team.pending", language)}
                                                </div>
                                            </div>
                                            {member.bio ? (
                                                <div className="text-xs text-muted-foreground">{member.bio}</div>
                                            ) : null}
                                            {member.verifiedBy ? (
                                                <div className="text-xs text-muted-foreground">
                                                    {t("team.verifiedBy", language)} {member.verifiedBy}
                                                </div>
                                            ) : null}
                                            {isAdmin ? (
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        className="rounded-2xl"
                                                        onClick={() => beginEditMember(member)}
                                                        disabled={editingMemberId === member.id}
                                                    >
                                                        {t("accountPage.team.actions.edit", language)}
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        className="rounded-2xl"
                                                        onClick={() => handleToggleApproved(member)}
                                                    >
                                                        {member.approved
                                                            ? t("accountPage.team.actions.markPending", language)
                                                            : t("accountPage.team.actions.approve", language)}
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="destructive"
                                                        className="rounded-2xl"
                                                        onClick={() => handleDelete(member)}
                                                    >
                                                        {t("accountPage.team.actions.remove", language)}
                                                    </Button>
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {isAdmin ? (
                    <Card className="rounded-3xl shadow-sm">
                        <CardHeader>
                            <CardTitle className="text-xl">
                                {t(
                                    editingMemberId
                                        ? "accountPage.addMember.editTitle"
                                        : "accountPage.addMember.title",
                                    language
                                )}
                            </CardTitle>
                            <p className="text-sm text-muted-foreground">
                                {t(
                                    editingMemberId
                                        ? "accountPage.addMember.editSubtitle"
                                        : "accountPage.addMember.subtitle",
                                    language
                                )}
                            </p>
                        </CardHeader>
                        <CardContent>
                            {editingMemberId ? (
                                <p className="mb-4 text-xs text-muted-foreground">
                                    {t("accountPage.addMember.editing", language).replace(
                                        "{name}",
                                        form.name.trim() || t("team.member", language)
                                    )}
                                </p>
                            ) : null}
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div>
                                        <label className="text-sm font-medium" htmlFor="member-id">
                                            {t("accountPage.addMember.fields.id", language)}
                                        </label>
                                        <Input
                                            id="member-id"
                                            name="id"
                                            required={!editingMemberId}
                                            value={form.id}
                                            disabled={!!editingMemberId}
                                            onChange={handleFormChange}
                                            placeholder={t("accountPage.addMember.placeholders.id", language)}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium" htmlFor="member-name">
                                            {t("contact.name", language)}
                                        </label>
                                        <Input
                                            id="member-name"
                                            name="name"
                                            required
                                            value={form.name}
                                            onChange={handleFormChange}
                                            placeholder={t("accountPage.addMember.placeholders.name", language)}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium" htmlFor="member-prefix">
                                            {t("accountPage.addMember.fields.prefix", language)}
                                        </label>
                                        <Input
                                            id="member-prefix"
                                            name="prefix"
                                            value={form.prefix}
                                            onChange={handleFormChange}
                                            placeholder={t("accountPage.addMember.placeholders.prefix", language)}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium" htmlFor="member-email">
                                            {t("contact.email", language)}
                                        </label>
                                        <Input
                                            id="member-email"
                                            name="email"
                                            type="email"
                                            required
                                            value={form.email}
                                            onChange={handleFormChange}
                                            placeholder={t("accountPage.addMember.placeholders.email", language)}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium" htmlFor="member-role">
                                            {t("accountPage.addMember.fields.role", language)}
                                        </label>
                                        <select
                                            id="member-role"
                                            name="role"
                                            value={form.role}
                                            onChange={handleFormChange}
                                            className="mt-1 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:bg-slate-900"
                                        >
                                            <option value="member">{t("team.member", language)}</option>
                                            <option value="admin">{t("auth.admin", language)}</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium" htmlFor="member-verifiedBy">
                                            {t("accountPage.addMember.fields.verifiedBy", language)}
                                        </label>
                                        <Input
                                            id="member-verifiedBy"
                                            name="verifiedBy"
                                            value={form.verifiedBy}
                                            onChange={handleFormChange}
                                            placeholder={t("accountPage.addMember.placeholders.verifiedBy", language)}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium" htmlFor="member-photoURL">
                                            {t("accountPage.addMember.fields.photoURL", language)}
                                        </label>
                                        <Input
                                            id="member-photoURL"
                                            name="photoURL"
                                            value={form.photoURL}
                                            onChange={handleFormChange}
                                            placeholder={t("accountPage.addMember.placeholders.photoURL", language)}
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="text-sm font-medium" htmlFor="member-bio">
                                            {t("accountPage.addMember.fields.bio", language)}
                                        </label>
                                        <Textarea
                                            id="member-bio"
                                            name="bio"
                                            rows={3}
                                            value={form.bio}
                                            onChange={handleFormChange}
                                            placeholder={t("accountPage.addMember.placeholders.bio", language)}
                                        />
                                    </div>
                                    <div className="flex items-center gap-2 pt-6">
                                        <input
                                            id="member-approved"
                                            name="approved"
                                            type="checkbox"
                                            checked={form.approved}
                                            onChange={handleFormChange}
                                            className="h-4 w-4 rounded border-slate-300"
                                        />
                                        <label className="text-sm" htmlFor="member-approved">
                                            {t("accountPage.addMember.fields.approved", language)}
                                        </label>
                                    </div>
                                </div>
                                <ErrorMessage message={formError} />
                                <SuccessMessage message={formSuccess} />
                                <div className="flex flex-wrap gap-2">
                                    <Button type="submit" className="rounded-2xl" disabled={pending}>
                                        {pending
                                            ? t(
                                                  editingMemberId
                                                      ? "accountPage.addMember.submit.editPending"
                                                      : "accountPage.addMember.submit.pending",
                                                  language
                                              )
                                            : t(
                                                  editingMemberId
                                                      ? "accountPage.addMember.submit.editAction"
                                                      : "accountPage.addMember.submit.action",
                                                  language
                                              )}
                                    </Button>
                                    {editingMemberId ? (
                                        <button
                                            type="button"
                                            onClick={cancelEditMember}
                                            disabled={pending}
                                            className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {t("accountPage.addMember.submit.cancel", language)}
                                        </button>
                                    ) : null}
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                ) : null}

                {isAdmin ? (
                    <ManageBooksSection
                        books={books}
                        createBook={createBook}
                        updateBook={updateBook}
                        deleteBook={deleteBook}
                    />
                ) : null}

                {isAdmin ? (
                    <ManageResourcesSection
                        resources={resources}
                        createResource={createResource}
                        updateResource={updateResource}
                        deleteResource={deleteResource}
                    />
                ) : null}
            </div>
        </section>
    );
}

function CoverPreview({ title, coverURL }) {
    const { language } = useLanguage();
    const initial = (title || "?").trim().charAt(0).toUpperCase() || "?";
    const altText = title
        ? `${title} ${t("accountPage.coverAltSuffix", language)}`
        : t("accountPage.coverAltFallback", language);
    if (coverURL) {
        return (
            <img
                src={coverURL}
                alt={altText}
                className="h-16 w-16 rounded object-cover"
                loading="lazy"
                onError={(event) => {
                    event.currentTarget.style.display = "none";
                }}
            />
        );
    }
    return (
        <div className="h-16 w-16 rounded bg-primary/10 text-primary flex items-center justify-center font-semibold">
            {initial}
        </div>
    );
}

function TagPills({ tags }) {
    const list = Array.isArray(tags) ? tags : [];
    if (list.length === 0) return null;
    return (
        <div className="mt-2 flex flex-wrap gap-1">
            {list.map((tag) => (
                <span
                    key={tag}
                    className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-slate-800/70 dark:text-slate-200"
                >
                    {tag}
                </span>
            ))}
        </div>
    );
}

function ManageBooksSection({ books, createBook, updateBook, deleteBook }) {
    const { language } = useLanguage();
    const initialForm = {
        title: "",
        author: "",
        description: "",
        coverURL: "",
        linkURL: "",
        tags: ""
    };
    const [form, setForm] = useState(initialForm);
    const [formError, setFormError] = useState("");
    const [formSuccess, setFormSuccess] = useState("");
    const [pending, setPending] = useState(false);

    const [listError, setListError] = useState("");
    const [listSuccess, setListSuccess] = useState("");

    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState(initialForm);
    const [editError, setEditError] = useState("");
    const [editPending, setEditPending] = useState(false);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleCreate = async (event) => {
        event.preventDefault();
        setFormError("");
        setFormSuccess("");
        setListError("");
        setListSuccess("");
        if (!form.title.trim()) {
            setFormError(t("accountPage.books.errors.titleRequired", language));
            return;
        }
        if (form.coverURL && !isHttpUrl(form.coverURL)) {
            setFormError(t("accountPage.books.errors.coverUrlInvalid", language));
            return;
        }
        if (form.linkURL && !isHttpUrl(form.linkURL)) {
            setFormError(t("accountPage.books.errors.linkUrlInvalid", language));
            return;
        }
        setPending(true);
        const payload = {
            title: form.title.trim(),
            author: form.author.trim(),
            description: form.description.trim(),
            coverURL: form.coverURL.trim(),
            linkURL: form.linkURL.trim(),
            tags: parseTags(form.tags)
        };
        const result = await createBook(payload);
        setPending(false);
        if (!result.ok) {
            setFormError(result.error);
            return;
        }
        setFormSuccess(t("accountPage.books.success.created", language));
        setForm(initialForm);
    };

    const beginEdit = (book) => {
        setEditingId(book.id);
        setEditForm({
            title: book.title || "",
            author: book.author || "",
            description: book.description || "",
            coverURL: book.coverURL || "",
            linkURL: book.linkURL || "",
            tags: Array.isArray(book.tags) ? book.tags.join(", ") : ""
        });
        setEditError("");
        setListError("");
        setListSuccess("");
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditForm(initialForm);
        setEditError("");
    };

    const handleEditChange = (event) => {
        const { name, value } = event.target;
        setEditForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleEditSubmit = async (event) => {
        event.preventDefault();
        if (!editingId) return;
        setEditError("");
        setListError("");
        setListSuccess("");
        if (!editForm.title.trim()) {
            setEditError(t("accountPage.books.errors.titleRequired", language));
            return;
        }
        if (editForm.coverURL && !isHttpUrl(editForm.coverURL)) {
            setEditError(t("accountPage.books.errors.coverUrlInvalid", language));
            return;
        }
        if (editForm.linkURL && !isHttpUrl(editForm.linkURL)) {
            setEditError(t("accountPage.books.errors.linkUrlInvalid", language));
            return;
        }
        setEditPending(true);
        const payload = {
            title: editForm.title.trim(),
            author: editForm.author.trim(),
            description: editForm.description.trim(),
            coverURL: editForm.coverURL.trim(),
            linkURL: editForm.linkURL.trim(),
            tags: parseTags(editForm.tags)
        };
        const result = await updateBook(editingId, payload);
        setEditPending(false);
        if (!result.ok) {
            setEditError(result.error);
            return;
        }
        setListSuccess(t("accountPage.books.success.updated", language));
        cancelEdit();
    };

    const handleDelete = async (book) => {
        setListError("");
        setListSuccess("");
        const result = await deleteBook(book.id);
        if (!result.ok) {
            setListError(result.error);
            return;
        }
        const fallback = t("accountPage.books.success.fallbackTitle", language);
        setListSuccess(
            t("accountPage.books.success.removed", language).replace(
                "{title}",
                book.title || fallback
            )
        );
        if (editingId === book.id) {
            cancelEdit();
        }
    };

    return (
        <Card className="rounded-3xl shadow-sm">
            <CardHeader>
                <CardTitle className="text-xl">{t("accountPage.books.title", language)}</CardTitle>
                <p className="text-sm text-muted-foreground">{t("accountPage.books.subtitle", language)}</p>
            </CardHeader>
            <CardContent className="space-y-6">
                <form onSubmit={handleCreate} className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                        <div>
                            <label className="text-sm font-medium" htmlFor="book-title">
                                {t("accountPage.books.fields.title", language)}
                            </label>
                            <Input
                                id="book-title"
                                name="title"
                                required
                                value={form.title}
                                onChange={handleChange}
                                placeholder={t("accountPage.books.placeholders.title", language)}
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium" htmlFor="book-author">
                                {t("accountPage.books.fields.author", language)}
                            </label>
                            <Input
                                id="book-author"
                                name="author"
                                value={form.author}
                                onChange={handleChange}
                                placeholder={t("accountPage.books.placeholders.author", language)}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="text-sm font-medium" htmlFor="book-description">
                                {t("accountPage.books.fields.description", language)}
                            </label>
                            <Textarea
                                id="book-description"
                                name="description"
                                rows={3}
                                value={form.description}
                                onChange={handleChange}
                                placeholder={t("accountPage.books.placeholders.description", language)}
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium" htmlFor="book-coverURL">
                                {t("accountPage.books.fields.coverURL", language)}
                            </label>
                            <Input
                                id="book-coverURL"
                                name="coverURL"
                                value={form.coverURL}
                                onChange={handleChange}
                                placeholder={t("accountPage.books.placeholders.coverURL", language)}
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium" htmlFor="book-linkURL">
                                {t("accountPage.books.fields.linkURL", language)}
                            </label>
                            <Input
                                id="book-linkURL"
                                name="linkURL"
                                value={form.linkURL}
                                onChange={handleChange}
                                placeholder={t("accountPage.books.placeholders.linkURL", language)}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="text-sm font-medium" htmlFor="book-tags">
                                {t("accountPage.books.fields.tags", language)}
                            </label>
                            <Input
                                id="book-tags"
                                name="tags"
                                value={form.tags}
                                onChange={handleChange}
                                placeholder={t("accountPage.books.placeholders.tags", language)}
                            />
                        </div>
                    </div>
                    <ErrorMessage message={formError} />
                    <SuccessMessage message={formSuccess} />
                    <Button type="submit" className="rounded-2xl" disabled={pending}>
                        {pending
                            ? t("accountPage.books.actions.addPending", language)
                            : t("accountPage.books.actions.add", language)}
                    </Button>
                </form>

                <div className="space-y-4">
                    <ErrorMessage message={listError} />
                    <SuccessMessage message={listSuccess} />
                    {books.length === 0 ? (
                        <p className="text-sm text-muted-foreground">{t("accountPage.books.listEmpty", language)}</p>
                    ) : (
                        <div className="space-y-4">
                            {books.map((book) => (
                                <div key={book.id} className="rounded-2xl border border-slate-200 p-4 dark:border-slate-800">
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                                        <CoverPreview title={book.title} coverURL={book.coverURL} />
                                        <div className="flex-1 space-y-1">
                                            <div className="font-medium text-base">{book.title}</div>
                                            {book.author ? (
                                                <div className="text-sm text-muted-foreground">
                                                    {t("resources.by", language)} {book.author}
                                                </div>
                                            ) : null}
                                            {book.description ? (
                                                <p className="text-sm text-muted-foreground">{book.description}</p>
                                            ) : null}
                                            {book.linkURL ? (
                                                <a
                                                    href={book.linkURL}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm font-medium text-primary hover:underline"
                                                >
                                                    {t("resources.view", language)}
                                                </a>
                                            ) : (
                                                <span className="text-sm text-muted-foreground">{t("resources.noLink", language)}</span>
                                            )}
                                            <TagPills tags={book.tags} />
                                        </div>
                                    </div>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        <Button type="button" size="sm" className="rounded-2xl" onClick={() => beginEdit(book)}>
                                            {t("accountPage.books.actions.edit", language)}
                                        </Button>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="destructive"
                                            className="rounded-2xl"
                                            onClick={() => handleDelete(book)}
                                        >
                                            {t("accountPage.books.actions.delete", language)}
                                        </Button>
                                    </div>
                                    {editingId === book.id ? (
                                        <form onSubmit={handleEditSubmit} className="mt-4 space-y-3">
                                            <div className="grid gap-3 md:grid-cols-2">
                                                <div>
                                                    <label className="text-sm font-medium" htmlFor={`edit-book-title-${book.id}`}>
                                                        {t("accountPage.books.fields.title", language)}
                                                    </label>
                                                    <Input
                                                        id={`edit-book-title-${book.id}`}
                                                        name="title"
                                                        required
                                                        value={editForm.title}
                                                        onChange={handleEditChange}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium" htmlFor={`edit-book-author-${book.id}`}>
                                                        {t("accountPage.books.fields.author", language)}
                                                    </label>
                                                    <Input
                                                        id={`edit-book-author-${book.id}`}
                                                        name="author"
                                                        value={editForm.author}
                                                        onChange={handleEditChange}
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="text-sm font-medium" htmlFor={`edit-book-description-${book.id}`}>
                                                        {t("accountPage.books.fields.description", language)}
                                                    </label>
                                                    <Textarea
                                                        id={`edit-book-description-${book.id}`}
                                                        name="description"
                                                        rows={3}
                                                        value={editForm.description}
                                                        onChange={handleEditChange}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium" htmlFor={`edit-book-coverURL-${book.id}`}>
                                                        {t("accountPage.books.fields.coverURL", language)}
                                                    </label>
                                                    <Input
                                                        id={`edit-book-coverURL-${book.id}`}
                                                        name="coverURL"
                                                        value={editForm.coverURL}
                                                        onChange={handleEditChange}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium" htmlFor={`edit-book-linkURL-${book.id}`}>
                                                        {t("accountPage.books.fields.linkURL", language)}
                                                    </label>
                                                    <Input
                                                        id={`edit-book-linkURL-${book.id}`}
                                                        name="linkURL"
                                                        value={editForm.linkURL}
                                                        onChange={handleEditChange}
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="text-sm font-medium" htmlFor={`edit-book-tags-${book.id}`}>
                                                        {t("accountPage.books.fields.tags", language)}
                                                    </label>
                                                    <Input
                                                        id={`edit-book-tags-${book.id}`}
                                                        name="tags"
                                                        value={editForm.tags}
                                                        onChange={handleEditChange}
                                                    />
                                                </div>
                                            </div>
                                            <ErrorMessage message={editError} />
                                            <div className="flex flex-wrap gap-2">
                                                <Button type="submit" className="rounded-2xl" disabled={editPending}>
                                                    {editPending
                                                        ? t("accountPage.books.actions.savePending", language)
                                                        : t("accountPage.books.actions.save", language)}
                                                </Button>
                                                <Button type="button" variant="outline" className="rounded-2xl" onClick={cancelEdit}>
                                                    {t("accountPage.books.actions.cancel", language)}
                                                </Button>
                                            </div>
                                        </form>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

function ManageResourcesSection({ resources, createResource, updateResource, deleteResource }) {
    const { language } = useLanguage();
    const initialForm = {
        title: "",
        description: "",
        type: "article",
        coverURL: "",
        linkURL: "",
        tags: ""
    };
    const [form, setForm] = useState(initialForm);
    const [formError, setFormError] = useState("");
    const [formSuccess, setFormSuccess] = useState("");
    const [pending, setPending] = useState(false);

    const [listError, setListError] = useState("");
    const [listSuccess, setListSuccess] = useState("");

    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState(initialForm);
    const [editError, setEditError] = useState("");
    const [editPending, setEditPending] = useState(false);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleCreate = async (event) => {
        event.preventDefault();
        setFormError("");
        setFormSuccess("");
        setListError("");
        setListSuccess("");
        if (!form.title.trim()) {
            setFormError(t("accountPage.resources.errors.titleRequired", language));
            return;
        }
        if (form.coverURL && !isHttpUrl(form.coverURL)) {
            setFormError(t("accountPage.resources.errors.coverUrlInvalid", language));
            return;
        }
        if (form.linkURL && !isHttpUrl(form.linkURL)) {
            setFormError(t("accountPage.resources.errors.linkUrlInvalid", language));
            return;
        }
        const type = form.type || "link";
        setPending(true);
        const payload = {
            title: form.title.trim(),
            description: form.description.trim(),
            type,
            coverURL: form.coverURL.trim(),
            linkURL: form.linkURL.trim(),
            tags: parseTags(form.tags)
        };
        const result = await createResource(payload);
        setPending(false);
        if (!result.ok) {
            setFormError(result.error);
            return;
        }
        setFormSuccess(t("accountPage.resources.success.created", language));
        setForm(initialForm);
    };

    const beginEdit = (resource) => {
        setEditingId(resource.id);
        setEditForm({
            title: resource.title || "",
            description: resource.description || "",
            type: resource.type || "link",
            coverURL: resource.coverURL || "",
            linkURL: resource.linkURL || "",
            tags: Array.isArray(resource.tags) ? resource.tags.join(", ") : ""
        });
        setEditError("");
        setListError("");
        setListSuccess("");
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditForm(initialForm);
        setEditError("");
    };

    const handleEditChange = (event) => {
        const { name, value } = event.target;
        setEditForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleEditSubmit = async (event) => {
        event.preventDefault();
        if (!editingId) return;
        setEditError("");
        setListError("");
        setListSuccess("");
        if (!editForm.title.trim()) {
            setEditError(t("accountPage.resources.errors.titleRequired", language));
            return;
        }
        if (editForm.coverURL && !isHttpUrl(editForm.coverURL)) {
            setEditError(t("accountPage.resources.errors.coverUrlInvalid", language));
            return;
        }
        if (editForm.linkURL && !isHttpUrl(editForm.linkURL)) {
            setEditError(t("accountPage.resources.errors.linkUrlInvalid", language));
            return;
        }
        const type = editForm.type || "link";
        setEditPending(true);
        const payload = {
            title: editForm.title.trim(),
            description: editForm.description.trim(),
            type,
            coverURL: editForm.coverURL.trim(),
            linkURL: editForm.linkURL.trim(),
            tags: parseTags(editForm.tags)
        };
        const result = await updateResource(editingId, payload);
        setEditPending(false);
        if (!result.ok) {
            setEditError(result.error);
            return;
        }
        setListSuccess(t("accountPage.resources.success.updated", language));
        cancelEdit();
    };

    const handleDelete = async (resource) => {
        setListError("");
        setListSuccess("");
        const result = await deleteResource(resource.id);
        if (!result.ok) {
            setListError(result.error);
            return;
        }
        const fallback = t("accountPage.resources.success.fallbackTitle", language);
        setListSuccess(
            t("accountPage.resources.success.removed", language).replace(
                "{title}",
                resource.title || fallback
            )
        );
        if (editingId === resource.id) {
            cancelEdit();
        }
    };

    const resourceTypes = ["article", "video", "pdf", "link"];

    return (
        <Card className="rounded-3xl shadow-sm">
            <CardHeader>
                <CardTitle className="text-xl">{t("accountPage.resources.title", language)}</CardTitle>
                <p className="text-sm text-muted-foreground">{t("accountPage.resources.subtitle", language)}</p>
            </CardHeader>
            <CardContent className="space-y-6">
                <form onSubmit={handleCreate} className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                        <div>
                            <label className="text-sm font-medium" htmlFor="resource-title">
                                {t("accountPage.resources.fields.title", language)}
                            </label>
                            <Input
                                id="resource-title"
                                name="title"
                                required
                                value={form.title}
                                onChange={handleChange}
                                placeholder={t("accountPage.resources.placeholders.title", language)}
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium" htmlFor="resource-type">
                                {t("accountPage.resources.fields.type", language)}
                            </label>
                            <select
                                id="resource-type"
                                name="type"
                                value={form.type}
                                onChange={handleChange}
                                className="mt-1 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:bg-slate-900"
                            >
                                {resourceTypes.map((typeOption) => (
                                    <option key={typeOption} value={typeOption}>
                                        {t(`accountPage.resources.types.${typeOption}`, language)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="text-sm font-medium" htmlFor="resource-description">
                                {t("accountPage.resources.fields.description", language)}
                            </label>
                            <Textarea
                                id="resource-description"
                                name="description"
                                rows={3}
                                value={form.description}
                                onChange={handleChange}
                                placeholder={t("accountPage.resources.placeholders.description", language)}
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium" htmlFor="resource-coverURL">
                                {t("accountPage.resources.fields.coverURL", language)}
                            </label>
                            <Input
                                id="resource-coverURL"
                                name="coverURL"
                                value={form.coverURL}
                                onChange={handleChange}
                                placeholder={t("accountPage.resources.placeholders.coverURL", language)}
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium" htmlFor="resource-linkURL">
                                {t("accountPage.resources.fields.linkURL", language)}
                            </label>
                            <Input
                                id="resource-linkURL"
                                name="linkURL"
                                value={form.linkURL}
                                onChange={handleChange}
                                placeholder={t("accountPage.resources.placeholders.linkURL", language)}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="text-sm font-medium" htmlFor="resource-tags">
                                {t("accountPage.resources.fields.tags", language)}
                            </label>
                            <Input
                                id="resource-tags"
                                name="tags"
                                value={form.tags}
                                onChange={handleChange}
                                placeholder={t("accountPage.resources.placeholders.tags", language)}
                            />
                        </div>
                    </div>
                    <ErrorMessage message={formError} />
                    <SuccessMessage message={formSuccess} />
                    <Button type="submit" className="rounded-2xl" disabled={pending}>
                        {pending
                            ? t("accountPage.resources.actions.addPending", language)
                            : t("accountPage.resources.actions.add", language)}
                    </Button>
                </form>

                <div className="space-y-4">
                    <ErrorMessage message={listError} />
                    <SuccessMessage message={listSuccess} />
                    {resources.length === 0 ? (
                        <p className="text-sm text-muted-foreground">{t("accountPage.resources.listEmpty", language)}</p>
                    ) : (
                        <div className="space-y-4">
                            {resources.map((resource) => (
                                <div key={resource.id} className="rounded-2xl border border-slate-200 p-4 dark:border-slate-800">
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                                        <CoverPreview title={resource.title} coverURL={resource.coverURL} />
                                        <div className="flex-1 space-y-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <div className="font-medium text-base">{resource.title}</div>
                                                {resource.type ? (
                                                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                                                        {t(`accountPage.resources.types.${resource.type}`, language) || resource.type}
                                                    </span>
                                                ) : null}
                                            </div>
                                            {resource.description ? (
                                                <p className="text-sm text-muted-foreground">{resource.description}</p>
                                            ) : null}
                                            {resource.linkURL ? (
                                                <a
                                                    href={resource.linkURL}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm font-medium text-primary hover:underline"
                                                >
                                                    {t("resources.view", language)}
                                                </a>
                                            ) : (
                                                <span className="text-sm text-muted-foreground">{t("resources.noLink", language)}</span>
                                            )}
                                            <TagPills tags={resource.tags} />
                                        </div>
                                    </div>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        <Button type="button" size="sm" className="rounded-2xl" onClick={() => beginEdit(resource)}>
                                            {t("accountPage.resources.actions.edit", language)}
                                        </Button>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="destructive"
                                            className="rounded-2xl"
                                            onClick={() => handleDelete(resource)}
                                        >
                                            {t("accountPage.resources.actions.delete", language)}
                                        </Button>
                                    </div>
                                    {editingId === resource.id ? (
                                        <form onSubmit={handleEditSubmit} className="mt-4 space-y-3">
                                            <div className="grid gap-3 md:grid-cols-2">
                                                <div>
                                                    <label className="text-sm font-medium" htmlFor={`edit-resource-title-${resource.id}`}>
                                                        {t("accountPage.resources.fields.title", language)}
                                                    </label>
                                                    <Input
                                                        id={`edit-resource-title-${resource.id}`}
                                                        name="title"
                                                        required
                                                        value={editForm.title}
                                                        onChange={handleEditChange}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium" htmlFor={`edit-resource-type-${resource.id}`}>
                                                        {t("accountPage.resources.fields.type", language)}
                                                    </label>
                                                    <select
                                                        id={`edit-resource-type-${resource.id}`}
                                                        name="type"
                                                        value={editForm.type}
                                                        onChange={handleEditChange}
                                                        className="mt-1 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:bg-slate-900"
                                                    >
                                                        {resourceTypes.map((typeOption) => (
                                                            <option key={typeOption} value={typeOption}>
                                                                {t(`accountPage.resources.types.${typeOption}`, language)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="text-sm font-medium" htmlFor={`edit-resource-description-${resource.id}`}>
                                                        {t("accountPage.resources.fields.description", language)}
                                                    </label>
                                                    <Textarea
                                                        id={`edit-resource-description-${resource.id}`}
                                                        name="description"
                                                        rows={3}
                                                        value={editForm.description}
                                                        onChange={handleEditChange}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium" htmlFor={`edit-resource-coverURL-${resource.id}`}>
                                                        {t("accountPage.resources.fields.coverURL", language)}
                                                    </label>
                                                    <Input
                                                        id={`edit-resource-coverURL-${resource.id}`}
                                                        name="coverURL"
                                                        value={editForm.coverURL}
                                                        onChange={handleEditChange}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium" htmlFor={`edit-resource-linkURL-${resource.id}`}>
                                                        {t("accountPage.resources.fields.linkURL", language)}
                                                    </label>
                                                    <Input
                                                        id={`edit-resource-linkURL-${resource.id}`}
                                                        name="linkURL"
                                                        value={editForm.linkURL}
                                                        onChange={handleEditChange}
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="text-sm font-medium" htmlFor={`edit-resource-tags-${resource.id}`}>
                                                        {t("accountPage.resources.fields.tags", language)}
                                                    </label>
                                                    <Input
                                                        id={`edit-resource-tags-${resource.id}`}
                                                        name="tags"
                                                        value={editForm.tags}
                                                        onChange={handleEditChange}
                                                    />
                                                </div>
                                            </div>
                                            <ErrorMessage message={editError} />
                                            <div className="flex flex-wrap gap-2">
                                                <Button type="submit" className="rounded-2xl" disabled={editPending}>
                                                    {editPending
                                                        ? t("accountPage.resources.actions.savePending", language)
                                                        : t("accountPage.resources.actions.save", language)}
                                                </Button>
                                                <Button type="button" variant="outline" className="rounded-2xl" onClick={cancelEdit}>
                                                    {t("accountPage.resources.actions.cancel", language)}
                                                </Button>
                                            </div>
                                        </form>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
